import React, { useState, useEffect } from "react";
import { Phone, Menu, X, Droplets } from "lucide-react";
import { SITE } from "@/lib/site";
import { Button } from "@/components/ui/button";

export const Header = ({ onQuoteClick }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#services", label: "Services" },
    { href: "#reviews", label: "Reviews" },
    { href: "#gallery", label: "Gallery" },
    { href: "#areas", label: "Areas" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-blue-600 grid place-items-center shadow-sm">
            <Droplets className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-slate-900 text-lg tracking-tight">
              ClearView
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-blue-600 font-bold -mt-0.5">
              Pressure Washing
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={SITE.phoneHref}
            data-testid="header-phone"
            className="flex items-center gap-2 text-sm font-bold text-slate-900 hover:text-blue-600 transition-colors"
          >
            <Phone className="h-4 w-4" />
            {SITE.phone}
          </a>
          <Button
            data-testid="header-quote-btn"
            onClick={onQuoteClick}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-sm hover:-translate-y-0.5 transition-all"
          >
            Get Free Quote
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-slate-900"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block px-3 py-3 rounded-lg text-slate-800 font-semibold hover:bg-slate-50"
              >
                {l.label}
              </a>
            ))}
            <div className="pt-3 grid grid-cols-2 gap-2">
              <a
                href={SITE.phoneHref}
                className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-slate-100 text-slate-900 font-bold text-sm"
              >
                <Phone className="h-4 w-4" /> Call
              </a>
              <button
                data-testid="mobile-quote-btn"
                onClick={() => {
                  setOpen(false);
                  onQuoteClick?.();
                }}
                className="px-3 py-3 rounded-xl bg-blue-600 text-white font-bold text-sm"
              >
                Get Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
